import { useEffect, useState } from 'react';
import { api, darf, datum, type Anmelder } from '../api.ts';
import { Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import Abwurf from '../teile/Abwurf.tsx';
import { Mailvorlagen } from '../teile/Mailvorlagen.tsx';
import { AuftragsinfoVorlagen } from '../teile/AuftragsinfoVorlagen.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { fragen } from '../teile/rueckfrage.tsx';

// ============================================================== Vordrucke
//
// Alle Vordrucke der Behörden, an einer Stelle.
//
// ORIGINALTEXT MARVIN (27.08.2026)
//   „Wo liegen denn die Vorlagen zu den Behörden? Da muss es ja ein
//    Verzeichnis zu geben wo ich sie hochladen kann. Auch per drag and drop."
//   und einen Tag später, zum ZWEITEN Mal:
//   „so, nochmal die frage, wo kommen die Vorlagendokumente rein?"
//
// DIE ERSTE ANTWORT WAR EIN REITER, UND SIE IST NICHT ANGEKOMMEN. Seit
// Lieferung .35 stand dieser Bildschirm in den Stammdaten - als SIEBENTER
// Reiter, hinter Gemeinden, Firmen, Leistungskatalog, Pollercodes, Ort und
// Gemeinde und Regelplänen. Wer nach einem ORT sucht, an dem Dateien liegen,
// macht keinen Bildschirm auf, auf dem Tabellen gepflegt werden.
//
// ER HAT WÖRTLICH NACH EINEM VERZEICHNIS GEFRAGT. Also bekommt es eines, mit
// einem eigenen Menüpunkt (Migration 052) - und der Reiter geht dabei weg.
// Zwei Wege zu derselben Sache sind kein doppelter Komfort, sondern eine
// halbe Auskunft: wer den einen kennt, erfährt vom anderen nie.
//
// DAS FELD IM GEMEINDEFENSTER BLEIBT. Dort wird ZUGEORDNET („welchen Vordruck
// will Limburg"), hier wird VERWALTET („welche haben wir"). Das sind zwei
// Fragen, und die erste stellt man mit der Gemeinde vor sich.
//
// UND EINES SAGT DIESER BILDSCHIRM AUSDRÜCKLICH: WAS MIT DEN VORDRUCKEN
// GESCHIEHT. Bis zur Lieferung .38 stand hier der Satz, dass sie NICHT
// befüllt werden - das war ehrlich und war eine Lücke. Seit .38 werden sie
// wirklich befüllt, und seitdem steht dieser Satz da. Ein Bildschirm, der
// eine Datei entgegennimmt und verschweigt, was mit ihr passiert, ist
// schlimmer als keiner - man lädt sie hoch und verlässt sich darauf.
//
// SEIT DEM 27.08.2026 STEHT AUCH DIE ANDERE RICHTUNG DA. Marvin: „was ist mit
// der verknüpfung zu den behörden?" Die Verknüpfung gab es; sie war nur in
// zwei Hälften auf zwei Bildschirmen verteilt. Unten steht sie zusammen:
// welche Gemeinde bekommt welchen Vordruck, und für welche liegt nichts
// bereit.

const VORDRUCKARTEN = [
  { art: 'AAZ', titel: 'Aufgrabungsanzeige (AAZ)',
    zweck: 'Sie zeigt der Gemeinde an, dass gegraben wird.' },
  { art: 'VAO', titel: 'Verkehrsrechtliche Anordnung (VAO)',
    zweck: 'Der Antrag auf die Anordnung. Bei vielen Gemeinden ist er '
         + 'zugleich der Gebührenbescheid.' },
  { art: 'FAZ', titel: 'Fertigstellungsanzeige (FAZ)',
    zweck: 'Sie meldet der Gemeinde, dass die Baustelle zurückgebaut ist.' },
  { art: 'POLLER', titel: 'Pollerantrag',
    zweck: 'Der Antrag auf das Befahren einer Fußgängerzone. Der Bescheid '
         + 'dazu ist ein anderes Papier.' },
];

/**
 * Vorlagen: Behördenvordrucke UND Mailtexte.
 *
 * ORIGINALTEXT MARVIN (27.08.2026)
 *   „gestalte es innovativer, weil wir haben ja auch unsere ganzen email
 *    textvorlagen. da sind so nicht sauber dargstellt. an der seite müssen die
 *    verfügbaren platzhalter auch immer stehen mit beispielen. das ist
 *    notwendig."
 *
 * EIN BEHÖRDENVORDRUCK UND EIN MAILTEXT SIND DIESELBE SORTE SACHE: eine
 * Vorlage, in die Werte eingesetzt werden. Der eine ist eine Word-Datei mit
 * `{{massnahme_text}}`, der andere ein Brief mit `{kunde}` - und beide gehen
 * an jemanden hinaus, der nicht bei uns arbeitet.
 *
 * Die Mailtexte lagen bis heute an DREI Orten und an keinem davon zum Lesen:
 * die vier Kundenmails im Nachrichtendialog EINES Auftrags (und dort nur die
 * eine, die man gerade sendet), der Telekom-Text unter
 * `GLASFASER_VERSAND_TEXT` in der Konfiguration - in einem einzeiligen
 * Textfeld, in dem ein sechszeiliger Brief steht -, und die Einladungsmail
 * kam mit Migration 054 dazu.
 *
 * Der Menüpunkt heisst seit Migration 054 „Vorlagen" und nicht mehr
 * „Vordrucke".
 */
export default function Vorlagen({ ich }: { ich: Anmelder }) {
  const [reiter, setReiter] = useState<'vordrucke' | 'mail' | 'auftragsinfo'>('vordrucke');
  return (
    <>
      <div className="reiter">
        <button onClick={() => setReiter('vordrucke')}
                aria-current={reiter === 'vordrucke'}>
          Behördenvordrucke
        </button>
        <button onClick={() => setReiter('mail')}
                aria-current={reiter === 'mail'}>
          Mailvorlagen
        </button>
        <button onClick={() => setReiter('auftragsinfo')}
                aria-current={reiter === 'auftragsinfo'}>
          Auftragsinfo
        </button>
      </div>
      {reiter === 'vordrucke'
        ? <Vordrucke ich={ich} />
        : reiter === 'mail' ? <Mailvorlagen ich={ich} />
        : <AuftragsinfoVorlagen ich={ich} />}
    </>
  );
}


function Vordrucke({ ich }: { ich: Anmelder }) {
  const [alle, setAlle] = useState<any[] | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [befund, setBefund] = useState<any>(null);
  const [zuordnung, setZuordnung] = useState<any>(null);
  const [marken, setMarken] = useState<any>(null);
  const darfLaden = darf(ich, 'vorlage.pflegen');

  /* DIE ZUORDNUNG WIRD MITGELADEN UND NICHT AUF KNOPFDRUCK.
     Marvin, 27.08.2026: „was ist mit der verknüpfung zu den behörden?"
     Eine Auskunft, die man erst anfordern muss, beantwortet die Frage nicht -
     sie stellt sie nur noch einmal. Es sind sechsundzwanzig Gemeinden, das
     kostet eine Abfrage. */
  const laden = () => Promise.all([
    api.vorlagenAlle().then(setAlle),
    api.vorlagenZuordnung().then(setZuordnung).catch(() => {}),
    api.vorlagenMarken().then(setMarken).catch(() => {}),
  ]).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  async function hoch(art: string, datei: File) {
    setLaeuft(art); setFehler(null); setBefund(null);
    try {
      setBefund(await api.vorlageHochladen(datei, art, ''));
      await laden();
    } catch (f: any) {
      /* GIBT ES SIE SCHON, WIRD GEFRAGT, OB DIE DATEI DORT GETAUSCHT WIRD.
         Vorher stand nur der Fehler da (Bildschirmfoto vom 24.09.2026). */
      const name = datei.name.replace(/\.[a-z0-9]+$/i, '').toLowerCase();
      const schon = (alle ?? []).find((v) => v.art === art && String(v.name).toLowerCase() === name);
      if (schon && await fragen(`„${schon.name}" gibt es für ${art} schon. Die Datei dort austauschen?`)) {
        await tauschen(schon, datei);
      } else {
        setFehler(ausFehler(f));
      }
    }
    finally { setLaeuft(null); }
  }

  async function tauschen(v: any, datei: File) {
    setLaeuft(v.art); setFehler(null); setBefund(null);
    try { setBefund(await api.vorlageDateiTauschen(v.id, datei)); await laden(); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(null); }
  }

  async function ansehen(v: any) {
    setFehler(null);
    /* DAS FENSTER GEHT SOFORT AUF, sonst hält der Browser es für ein
       ungebetenes Fenster und blockiert es. */
    const fenster = window.open('', '_blank');
    try {
      const url = await api.vorlageAnsicht(v.id);
      if (fenster) fenster.location.href = url; else window.open(url, '_blank');
    } catch (f) {
      fenster?.close();
      setFehler(ausFehler(f));
    }
  }

  async function aufnehmen(v: any) {
    setFehler(null);
    try { await api.vorlageAendern(v.id, { aktiv: true }); await laden(); }
    catch (f) { setFehler(ausFehler(f)); }
  }

  async function loeschen(v: any) {
    if (!await fragen(`Vorlage „${v.name}" löschen? Das lässt sich nicht rückgängig machen.`)) return;
    setFehler(null);
    try { await api.vorlageLoeschen(v.id); await laden(); }
    catch (f) { setFehler(ausFehler(f)); }
  }

  async function stilllegen(v: any) {
    setFehler(null);
    try { await api.vorlageAendern(v.id, { aktiv: false }); await laden(); }
    catch (f) { setFehler(ausFehler(f)); }
  }

  const gesamt = (alle ?? []).filter((v) => v.aktiv !== false).length;

  return (
    <>
      <div className="kopf">
        <h1>Vordrucke</h1>
        <div className="wachsen" />
        <span className="klein leise">
          {alle === null ? 'Wird geladen.'
            : gesamt === 1 ? '1 Vordruck hinterlegt'
            : `${gesamt} Vordrucke hinterlegt`}
        </span>
      </div>

      {/* JEDER BILDSCHIRM HAT DENSELBEN SEITENRAND.

          ORIGINALTEXT MARVIN (27.08.2026)
            „und bitte im fram der darstellung an beiden seiten links und
             rechts etwas abstand halten. es wirkt sehr gequetscht. prüfe das
             auch mal bei anderen seiten und passe es an."

          Er hat es auf DIESEM Bildschirm bemängelt, und der Grund war nicht
          die Zahl, sondern eine fehlende Hülle: die meisten Bildschirme
          setzen ihren Leib in `<div className="inhalt">`, und der trägt den
          Rand. Vier taten es nicht - dieser, das Handbuch, das Postfach und
          der Umzug -, und dort lief der Inhalt bis an die Kante. Kein
          Prüfstand konnte das sehen: er misst, ob die Seite seitlich rollt,
          und bis an den Rand gefüllt rollt sie gerade nicht. */}
      <div className="inhalt">
        {/* DIE BEIDEN ERKLÄRKÄSTEN SIND WEG (.221). Marvin: „infoteste weg."
            Was sie sagten, steht im Handbuch unter „Vorlagen". */}
        <Fehlerkasten lage={fehler} />

        {befund && (
          <div className={'hinweis klein '
                          + ((befund.fehlende_felder ?? []).length ? 'warnung' : '')}
               style={{ marginBottom: 12 }}>
            {befund.meldung}
            {(befund.fehlende_felder ?? []).length > 0 && (
              <div style={{ marginTop: 4 }}>
                <b>Nicht gefüllt:</b> {befund.fehlende_felder.join(', ')}.
              </div>
            )}
          </div>
        )}

        {VORDRUCKARTEN.map((a) => {
          const drin = (alle ?? []).filter((v) => v.art === a.art);
          return (
            <div key={a.art} className="karte" style={{ marginBottom: 12 }}>
              <div className="reihe kopfzeile" style={{ gap: 8 }}>
                <b className="wachsen">{a.titel}</b>
                <span className="klein leise">
                  {drin.length
                    ? `${drin.length} ${drin.length === 1 ? 'Vordruck' : 'Vordrucke'}`
                    : 'keiner hinterlegt'}
                </span>
              </div>
              <p className="klein leise" style={{ margin: '2px 0 0' }}>{a.zweck}</p>

              {drin.length > 0 && (
                <div className="rollt">
                  <table className="tabelle" style={{ marginTop: 8 }}>
                    <thead>
                      <tr>
                        <th>Name</th><th>Datei</th><th>Marken</th>
                        <th>Gemeinden</th>
                        <th>Hochgeladen</th><th />
                      </tr>
                    </thead>
                    <tbody>
                      {drin.map((v) => (
                        <tr key={v.id} style={v.aktiv === false ? { opacity: 0.6 } : undefined}>
                          <td>
                            {v.name}
                            {v.aktiv === false && (
                              <span className="marke klein" style={{ marginLeft: 6 }}>stillgelegt</span>
                            )}
                          </td>
                          <td className="klein leise">{v.dateiname}</td>
                          <td className="klein">
                            {(v.platzhalter ?? []).length || '-'}
                          </td>
                          {/* WIE VIELE DARAUF ZEIGEN, IST DIE WARNUNG.
                              Wer einen Vordruck austauscht, den sechs Gemeinden
                              benutzen, ändert ihn für alle sechs. */}
                          <td className="klein">{v.gemeinden ?? 0}</td>
                          <td className="klein leise">{datum(v.hochgeladen_am)}</td>
                          <td style={{ width: 300 }}>
                            <div className="reihe" style={{ flexWrap: 'wrap', gap: 6 }}>
                              <button className="knopf klein" onClick={() => ansehen(v)}>
                                Ansehen
                              </button>
                              <a className="knopf klein" href={api.vorlageAdresse(v.id)} target="_blank" rel="noreferrer">
                                Herunterladen
                              </a>
                              {darfLaden && (
                                <label className="knopf klein" style={{ cursor: 'pointer' }}>
                                  Datei austauschen
                                  <input type="file" accept=".docx,.pdf" hidden
                                         onChange={(e) => {
                                           const x = e.target.files?.[0];
                                           e.target.value = '';
                                           if (x) tauschen(v, x);
                                         }} />
                                </label>
                              )}
                              {darfLaden && v.aktiv !== false && (
                                <button className="knopf klein"
                                        title={Number(v.gemeinden) > 0
                                          ? 'Solange Gemeinden darauf zeigen, '
                                            + 'geht das nicht'
                                          : 'Aus der Auswahl nehmen'}
                                        onClick={() => stilllegen(v)}>
                                  stilllegen
                                </button>
                              )}
                              {darfLaden && v.aktiv === false && (
                                <button className="knopf klein"
                                        title="Wieder in die Auswahl nehmen"
                                        onClick={() => aufnehmen(v)}>
                                  wieder aufnehmen
                                </button>
                              )}
                              {darfLaden && (
                                <button className="knopf klein rot"
                                        title={Number(v.gemeinden) > 0
                                          ? 'Solange Gemeinden darauf zeigen, geht das nicht'
                                          : 'Endgültig löschen'}
                                        onClick={() => loeschen(v)}>
                                  Löschen
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {darfLaden ? (
                <div style={{ marginTop: 8 }}>
                  <Abwurf
                    titel={`Vordruck für ${a.art} hierher`}
                    klein laeuft={laeuft === a.art} laufText="Wird gelesen"
                    accept=".docx,.pdf"
                    hinweis="Ziehen oder tippen. Word, PDF oder Text."
                    nimm={(x) => { if (x[0]) hoch(a.art, x[0].datei); }} />
                </div>
              ) : (
                <p className="klein leise" style={{ marginTop: 8 }}>
                  Zum Hochladen fehlt das Recht „Vordrucke pflegen".
                </p>
              )}
            </div>
          );
        })}

        <Marken m={marken} />
        <Zuordnung z={zuordnung} />
      </div>
    </>
  );
}

// ============================================ Welche Marke füllt dieses System
//
// ORIGINALTEXT MARVIN (09.09.2026, mit der erzeugten AAZ und VAO daneben)
//   „die erzeugten dokumente haben fehler, weil du nicht die variablen
//    platzhalter alle übernommen hast. prüfe auch alle anderen dokumente!
//    das muss jetzt als erstes geschehen weil wir nicht arbeiten können!"
//
// SEIN ZWEITER SATZ IST DER TEURERE. Die AAZ und die VAO lagen als PDF vor ihm
// und ließen sich abzählen; für die Fertigstellungsanzeige und den
// Pollerantrag lag keines da - und ein Papier, das erst bei der Behörde
// auffällt, ist der schlechteste Ort für einen Befund.
//
// DREI ZUSTÄNDE, DREI NÄCHSTE SCHRITTE, und der Unterschied ist der ganze
// Zweck dieser Karte: eine Marke, die der Katalog nicht kennt, bleibt in JEDEM
// Papier aus diesem Vordruck stehen; ein Pflichtfeld ohne Antwort hält den
// Versand an; ein freiwilliges Feld ohne Antwort ist ein leeres Kästchen und
// damit die Auskunft „nicht betroffen".

function Marken({ m }: { m: any }) {
  const [offen, setOffen] = useState(false);
  if (!m) return null;

  const zeilen: any[] = (m.vorlagen ?? []).filter((z: any) => z.befuellbar);
  const mitLuecke = zeilen.filter((z: any) => z.aktiv && z.unbekannt.length);
  /* NUR EINE ZAHL WÄRE KEINE AUSKUNFT. Ein Vordruck, der eine unbekannte
     Marke nennt, gehört ganz oben; die übrigen stehen darunter und klappen
     auf - eine Liste, die bei zehn Vordrucken immer vollständig dasteht,
     wird nach zwei Wochen nicht mehr gelesen. */
  const zeigen = offen ? zeilen : mitLuecke;

  return (
    <div className="karte">
      <h3>
        <span className={'zustandspunkt '
          + (zeilen.length === 0 ? 'aus'
             : mitLuecke.length ? 'hart' : 'gut')} />
        Was die Vordrucke verlangen
      </h3>
      <p className="klein leise">
        {m.klartext} Dieses System kennt {m.bekannt} Namen.
      </p>

      {zeigen.length === 0 ? (
        <p className="klein leise">
          {zeilen.length === 0
            ? 'Es liegt kein Vordruck zum Befüllen hier - ein PDF ist zum '
              + 'Beilegen da und wird nicht ausgefüllt.'
            : 'Kein Vordruck nennt eine unbekannte Marke.'}
        </p>
      ) : (
        <div className="rollt">
          <table className="liste">
            <thead>
              <tr>
                <th>Art</th><th>Vordruck</th><th>Marken</th>
                <th>Kennt das System nicht</th><th>Pflicht darin</th>
              </tr>
            </thead>
            <tbody>
              {zeigen.map((z: any) => (
                <tr key={z.id}>
                  <td className="klein">{z.art}</td>
                  <td>
                    {z.name}
                    {!z.aktiv && (
                      <span className="marke klein"
                            style={{ marginLeft: 6 }}>stillgelegt</span>
                    )}
                  </td>
                  <td className="klein">{z.gefuellt} von {z.nennt}</td>
                  <td className="klein">
                    {z.unbekannt.length === 0
                      ? <span className="leise">-</span>
                      : z.unbekannt.map((n: string) => (
                          <code key={n} style={{ marginRight: 6 }}>{`{${n}}`}</code>
                        ))}
                  </td>
                  {/* WELCHE PFLICHTFELDER DIESER VORDRUCK VERLANGT. Ohne
                      Antwort bleibt die Marke im Papier stehen, und dann geht
                      es nicht an die Behörde hinaus. */}
                  <td className="klein leise">{z.pflicht.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {zeilen.length > mitLuecke.length && (
        <button className="knopf klein" style={{ marginTop: 8 }}
                onClick={() => setOffen(!offen)}>
          {offen ? 'Nur die mit Lücke' : `Alle ${zeilen.length} anzeigen`}
        </button>
      )}

      <p className="klein leise" style={{ marginTop: 8 }}>
        <b>Was zu tun ist:</b> Eine Marke, die dieses System nicht kennt,
        gehört in den Katalog - sie bleibt sonst in jedem Papier aus diesem
        Vordruck stehen, und ein Papier mit einer rohen Marke geht nicht an
        eine Behörde hinaus. Ein Pflichtfeld ohne Antwort wird unter „Auftrag
        vorbereiten" eingetragen; ein freiwilliges Feld ohne Antwort bleibt
        leer, und ein leeres Kästchen heißt „nicht betroffen".
      </p>
    </div>
  );
}

// ================================================ Die Verknüpfung zur Behörde
//
// ORIGINALTEXT MARVIN (27.08.2026)
//   „was ist mit der verknüpfung zu den behörden?"
//
// SIE WAR DA UND WAR NICHT ZU SEHEN. Auf diesem Bildschirm stand je Vordruck
// eine ZAHL („3 Gemeinden"), in der Gemeindeakte je Gemeinde ein
// Auswahlfeld. Beides zusammen ergibt die Übersicht erst im Kopf des Lesers,
// und die eigentliche Frage beantwortet keines von beidem: welche Gemeinde
// bekommt gerade was - und für welche liegt gar nichts bereit.
//
// DER DRITTE ZUSTAND IST DER GRUND FÜR DIESE TABELLE. „Eigen" und „Standard"
// sind beide in Ordnung; „keiner" ist es nicht, und heute merkt es erst, wer
// das Papier abschickt. Er steht deshalb rot da und nicht als leere Zelle.

function Marke({ f }: { f: any }) {
  if (!f) return <span className="leise">-</span>;
  if (f.woher === 'eigen') {
    return <span className="marke-zustand z-erledigt">{f.name}</span>;
  }
  if (f.woher === 'einziger') {
    return (
      <span className="marke-zustand z-laufend"
            title="Diese Gemeinde nennt keinen eigenen Vordruck, und es liegt
                   genau einer dieser Art hier. Also gilt er.">
        {f.name}
      </span>
    );
  }
  /* „KEINER" HEISST ZWEIERLEI, und das sind zwei verschiedene nächste
     Schritte: es liegt gar keiner (dann hochladen), oder es liegen mehrere
     und niemand hat zugeordnet (dann in der Gemeindeakte wählen). Ein Wort
     für beides schickt den Leser in die falsche Richtung. */
  return (
    <span className="marke-zustand z-ueberfaellig"
          title={Number(f.vorhanden) > 1
            ? `Es liegen ${f.vorhanden} Vordrucke dieser Art, und diese `
              + 'Gemeinde nennt keinen. Das Papier entsteht ohne Vordruck.'
            : 'Es liegt kein Vordruck dieser Art. Das Papier entsteht aus '
              + 'dem Programm.'}>
      {Number(f.vorhanden) > 1 ? `keiner gewählt (${f.vorhanden})` : 'keiner'}
    </span>
  );
}

function Zuordnung({ z }: { z: any }) {
  if (!z) return null;
  const g = (z.gemeinden ?? []) as any[];
  const ohne = g.filter((x) =>
    x.aktiv && (x.vao?.woher === 'keiner' || x.poller?.woher === 'keiner'));
  /* HIER STAND EIN DRITTER KASTEN: „früher genannt, hier nicht
     verknüpft". Er zählte Gemeinden, an denen ein DATEINAME stand und keine
     Datei lag. Mit Migration 069 ist beides gefallen - der Name von früher
     und die Ablage, aus der die Datei nachgekommen wäre. */

  return (
    <div className="karte" style={{ marginBottom: 12 }}>
      <div className="reihe kopfzeile" style={{ gap: 8 }}>
        <b className="wachsen">Welche Behörde bekommt welchen Vordruck</b>
        <span className="klein leise">
          {g.length} {g.length === 1 ? 'Gemeinde' : 'Gemeinden'}
        </span>
      </div>

      <p className="klein leise" style={{ margin: '2px 0 8px' }}>
        Zugeordnet wird in der Gemeindeakte unter Stammdaten; hier steht, was
        dabei herausgekommen ist. Nennt eine Gemeinde keinen eigenen Vordruck,
        gilt der Standard ihrer Art.
      </p>

      {/* AAZ UND FAZ HABEN KEINE GEMEINDESPALTE, und das steht als Satz da
          und nicht als eine Spalte, die für die halbe Tabelle leer ist. */}
      <div className="hinweis klein" style={{ marginBottom: 8 }}>
        <b>AAZ und FAZ werden nicht je Gemeinde zugeordnet.</b> Für sie gilt
        der Vordruck, der als einziger seiner Art hier liegt:{' '}
        {(z.ohneGemeinde ?? []).map((o: any, i: number) => (
          <span key={o.art}>
            {i > 0 && ', '}
            {o.art}:{' '}
            {o.gilt
              ? <b>{o.gilt}</b>
              : <b className="rot">
                  {Number(o.vorhanden) > 1
                    ? `${o.vorhanden} liegen hier, keiner gewählt`
                    : 'keiner hinterlegt'}
                </b>}
          </span>
        ))}
      </div>

      {ohne.length > 0 && (
        <div className="hinweis klein warnung" style={{ marginBottom: 8 }}>
          <b>{ohne.length} aktive {ohne.length === 1 ? 'Gemeinde hat' : 'Gemeinden haben'}
          {' '}für mindestens eine Art keinen Vordruck.</b> Ein Papier für sie
          entsteht dann ohne Vordruck.
        </div>
      )}

      {g.length > 0 && (
        <div className="rollt">
          <table className="tabelle">
            <thead>
              <tr><th>Gemeinde</th><th>VAO</th><th>Pollerantrag</th></tr>
            </thead>
            <tbody>
              {g.map((x: any) => (
                <tr key={x.id}>
                  <td>
                    {x.name}
                    {!x.aktiv && <span className="leise klein"> nicht aktiv</span>}
                  </td>
                  <td className="klein"><Marke f={x.vao} /></td>
                  <td className="klein"><Marke f={x.poller} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {g.length === 0 && (
        <p className="klein leise">
          Es steht noch keine Gemeinde in den Stammdaten.
        </p>
      )}
    </div>
  );
}
